import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Flex } from 'reflexbox';

import {
  slotMap,
  invertedSlotMap,
  getWeaponHands,
  CRAFTED_STAT_COMBOS,
  getEmbellishment,
} from 'shared/items';
import { getEnchants } from 'shared/enchants';
import {
  getGemInfo,
} from 'shared/gems';
import {
  getSpecInfo,
} from 'shared/character';

import {
  WowItemIcon,
} from 'frontend/components';
import WowIcon, { sizeMap } from 'frontend/components/WowIcon';
import config from 'frontend/config';

const gemSize = 12;
const styles = {
  enchant: {
    fontFamily: 'Tahoma',
    width: '100%',
    position: 'absolute',
    bottom: -10,
    left: 0,
    right: 0,
    margin: 'auto',
    textAlign: 'center',
    fontSize: 10,
    color: config.colors.green,
  },
  gem: {
    position: 'absolute',
    top: -4,
    right: -4,
    height: gemSize,
    width: gemSize,
    padding: 1,
    lineHeight: 1,
  },
  flexWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    marginRight: 8,
  },
};

const GearStrip = ({
  equippedGear,
  fullGear,
  classId,
  specId,
  specName,
  showEmptySlots,
  showEnchants,
  showGems,
  hideCraftingQuality,
  overrideItems,
  size,
  overrideOnly,
  justify,
  ...rest
}) => {
  let equippedIds = [];

  const slotNameMap = {
    mainHand: 'main_hand',
    offHand: 'off_hand',
  };
  const gear = _.mapKeys(equippedGear, (val, key) => {
    if (val) {
      equippedIds.push(val.id);
    }
    return slotNameMap[key] || key;
  });

  if (fullGear) {
    equippedIds = _.map(fullGear, 'id');
  }

  if (!gear.off_hand) {
    gear.off_hand = null;
  }

  let resolvedGear = gear;
  if (overrideItems) {
    resolvedGear = _.mapValues(gear, (item, slot) => {
      const inventoryType = parseInt(invertedSlotMap[slot], 10);
      const overrideItem = _.find(overrideItems, it => {
        if (it.itemSlot) {
          return it.itemSlot === slot;
        }
        if (it.itemHand) {
          return it.itemHand === slot;
        }
        if (it.item) {
          // normalize slot ID for item being compared
          // this should handle the case of comparing a "chest" to a "robe"
          const normalizedType = parseInt(invertedSlotMap[slotMap[it.item.inventoryType]], 10);
          return normalizedType === inventoryType;
        }
        return null;
      });
      if (overrideItem) {
        const bonusLists = _.cloneDeep(overrideItem.bonusLists || overrideItem.item?.bonusLists || []);
        if (overrideItem.itemAffix) {
          bonusLists.push(overrideItem.itemAffix);
        }
        if (overrideItem.legendaryPower) {
          bonusLists.push(overrideItem.legendaryPower);
        }
        if (overrideItem.embellishment) {
          const embellishment = getEmbellishment(overrideItem.embellishment);
          _.each(embellishment.craftingBonusIds, bid => bonusLists.push(bid));
        }
        let craftedStats;
        if (overrideItem.craftedStats) {
          craftedStats = _.get(_.find(CRAFTED_STAT_COMBOS, { value: overrideItem.craftedStats }), 'id');
        }
        return {
          ...overrideItem.item,
          override: true,
          itemLevel: overrideItem.itemLevel,
          itemEnchant: overrideItem.itemEnchant,
          itemGem0: overrideItem.itemGem0,
          itemGem1: overrideItem.itemGem1,
          itemGem2: overrideItem.itemGem2,
          bonusLists,
          craftedStats,
        };
      }
      if (overrideOnly) {
        return null;
      }
      return item;
    });
  }

  const mainHandiness = getWeaponHands(resolvedGear.main_hand);
  const specInfo = getSpecInfo(classId, specName || specId);
  if (mainHandiness === '2h' && specInfo && !specInfo.dualWield2h) {
    resolvedGear.off_hand = null;
  }

  const sizePx = sizeMap[size] || size;

  const verticalWrapMargin = 10;

  const slots = [
    'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist',
    'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2', 'main_hand', 'off_hand',
  ];

  let first = true;

  return (
    <Flex
      align="center"
      justify={justify}
      style={{
        marginTop: -verticalWrapMargin,
      }}
      {...rest}
    >
      {_.map(slots, slot => {
        const item = resolvedGear[slot];

        if (!item && !showEmptySlots) {
          return null;
        }

        let props;
        if (overrideItems && item) {
          if (item.override) {
            props = {
              highlight: !overrideOnly, // highlight if showing all
            };
          } else {
            props = {
              dull: true,
            };
          }
        }

        let elEnchant;
        if (showEnchants && item && item.enchant_id) {
          const enchant = _.find(getEnchants(), { id: parseInt(item.enchant_id, 10) });
          if (enchant) {
            elEnchant = (
              <div
                style={{
                  ...styles.enchant,
                  fontSize: 10 * _.clamp((sizePx / 24), 0.7, 1),
                }}
              >
                {enchant.shortName || enchant.name}
              </div>
            );
          }
        }
        let elGem;
        if (showGems && item && item.gem_id) {
          const gems = _.split(item.gem_id, '/');
          const gemBonusIds = _.split(item.gem_bonus_id, '/');
          let border;
          if (item.addSocket) {
            border = `1px solid ${config.colors.blue}`;
          }
          if (_.size(gems) > 0) {
            elGem = (
              <div
                style={styles.gem}
              >
                {_.map(gems, (gemId, i) => {
                  const gem = _.find(getGemInfo(), { itemId: parseInt(gemId, 10) });
                  if (gem) {
                    return (
                      <WowIcon
                        key={`${i}-${gemId}`}
                        size={_.round(gemSize * _.clamp(sizePx / 24, 0.6, 1))}
                        id={gem.itemId}
                        type="item"
                        icon={gem.itemIcon}
                        pl={1}
                        border={border}
                        extra={{
                          bonus: gemBonusIds[i],
                        }}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            );
          }
        }

        let showDivider = false;
        if (!first && _.includes(['trinket1', 'main_hand'], slot)) {
          showDivider = true;
        }
        first = false;

        return (
          <React.Fragment key={slot}>
            <div
              key={slot}
              style={{
                ...styles.flexWrap,
                marginLeft: showDivider ? (0.6 * sizePx) : 0,
              }}
            >
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 1,
                    height: sizePx,
                    marginTop: verticalWrapMargin,
                    position: 'relative',
                  }}
                >
                  <WowItemIcon
                    item={item}
                    classId={classId}
                    specId={specInfo?.id}
                    size={size}
                    setPieceIds={equippedIds}
                    slot={slot}
                    hideCraftingQuality={hideCraftingQuality}
                    {...props}
                  />

                  {elEnchant}

                  {elGem}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </Flex>
  );
};

GearStrip.propTypes = {
  equippedGear: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  fullGear: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  classId: PropTypes.number,
  specId: PropTypes.number,
  specName: PropTypes.string,
  overrideItems: PropTypes.arrayOf(PropTypes.object),
  showEmptySlots: PropTypes.bool,
  showEnchants: PropTypes.bool,
  showGems: PropTypes.bool,
  hideCraftingQuality: PropTypes.bool,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  overrideOnly: PropTypes.bool,
  justify: PropTypes.string,
};

GearStrip.defaultProps = {
  equippedGear: null,
  fullGear: null,
  classId: null,
  specId: null,
  specName: null,
  showEmptySlots: false,
  showEnchants: false,
  showGems: false,
  hideCraftingQuality: false,
  overrideItems: null,
  size: 'medium',
  overrideOnly: false,
  justify: 'flex-start',
};

export default GearStrip;
